import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { useEmbedStore } from '@/stores/embed-store';
import { initiateLoginRedirect } from '@/lib/oauth';
import EmbedOnboarding from '@/embed/EmbedOnboarding';

const USE_DIRECT_AUTH = import.meta.env.VITE_USE_DIRECT_AUTH === 'true';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isEmbedded = useEmbedStore((s) => s.isEmbedded);
  const needsAdminAccess = useEmbedStore((s) => s.needsAdminAccess);
  const needsProvisioning = useEmbedStore((s) => s.needsProvisioning);

  // If not authenticated, redirect to login page or OptiPrime
  useEffect(() => {
    // In embed mode, don't redirect — wait for SYNC_AUTH from parent
    if (isEmbedded) return;

    if (!loading && !user) {
      // Capture current URL to return to after authentication
      const returnUrl = `${window.location.pathname}${window.location.search}`;

      if (USE_DIRECT_AUTH) {
        // Redirect to local login page with return_url
        const encodedReturnUrl = encodeURIComponent(returnUrl);
        navigate(`/auth/login?return_url=${encodedReturnUrl}`);
      } else {
        // Redirect to OptiPrime OAuth with return_url stored in sessionStorage
        initiateLoginRedirect(returnUrl);
      }
    }
  }, [loading, user, navigate, isEmbedded]);

  // Embed user whose OptiSigns role is below the sync threshold.
  // Their workspace was not provisioned — show a clear message instead of the app.
  if (isEmbedded && needsAdminAccess) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center">
        <div className="flex max-w-lg flex-col items-center gap-6 text-center">
          <h1 className="text-2xl font-semibold">Access Required</h1>
          <p className="text-muted-foreground">
            You don't have access to OptiDev. Contact your admin to get access.
          </p>
          <a
            href="https://optidev.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-muted-foreground hover:underline"
          >
            Learn more about OptiDev
          </a>
        </div>
      </div>
    );
  }

  // Admin/owner in embed mode with no workspace yet — show onboarding to provision one.
  if (isEmbedded && needsProvisioning && user) {
    return <EmbedOnboarding />;
  }

  if (loading || (isEmbedded && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Show minimal loading state while redirect is happening
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <>{children}</>;
}
